import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import { CreateProfil } from 'src/app/utils/interfaces/new/create-profil.interface';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { Profil } from 'src/app/utils/interfaces/new/profil.interface';
import { environment } from 'src/environments/environment';
import { FiltresService } from '../filtres/filtres.service';
import { SnackbarService } from '../snackbar/snackbar.service';

@Injectable({
  providedIn: 'root'
})
export class ProfilService {

  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders()
  };

  private _wait = (ms) => new Promise(res => setTimeout(res, ms));

  // Cache de useEdcRestProxy, interrogé à chaque appel DMP / INSi : invalidé au changement de profil ou après USE_EDC_REST_PROXY_CACHE_DELAY_MS
  private _useEdcRestProxyCache: { profilId: string | undefined, date: number, value: Promise<boolean> } | undefined;
  private static readonly USE_EDC_REST_PROXY_CACHE_DELAY_MS = 15 * 60 * 1000;

  constructor(
    private http: HttpClient,
    private _filtresService: FiltresService,
    private _snack: SnackbarService
  ) { }

  public async create(profil: CreateProfil): Promise<string> {
    try {
      const res = await this.http.post<string>(`${environment.basePath}/hypermed/profils`, profil, this.httpOptions).toPromise();
      return res.body;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async find(filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'username'
  }): Promise<Paginated<Profil>> {
    try {
      this.httpOptions.headers = this._filtresService.setHeaders(filtre);
      const res = await this.http.get(`${environment.basePath}/hypermed/profils`, this.httpOptions).toPromise();
      const profils = res.body as Profil[];
      const paginated: Paginated<Profil> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: profils
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findOne(id: string): Promise<Profil> {
    try {
      const res = await this.http.get(`${environment.basePath}/hypermed/profils/${id}`, this.httpOptions).toPromise();
      const profil = res.body as Profil;
      return profil;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async enableProfil(profil: Profil): Promise<void> {
    try {
      profil.disabled = false;
      const res = await this.http.put(`${environment.basePath}/hypermed/profils/${profil.id}`, profil, this.httpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async disableProfil(profil: Profil): Promise<void> {
    try {
      profil.disabled = true;
      const res = await this.http.put(`${environment.basePath}/hypermed/profils/${profil.id}`, profil, this.httpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async update(profil: Profil): Promise<void> {
    try {
      const res = await this.http.put(`${environment.basePath}/hypermed/profils/${profil.id}`, profil, this.httpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async assignUuidInstance(): Promise<string> {
    try {
      const res = await this.http.put(`${environment.basePath}/hypermed/profils/assignUuidInstance`, {}, this.httpOptions).toPromise();
      return res.body as string;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async updateMe(profil: Profil): Promise<void> {
    try {
      delete profil.actif;
      delete profil.cabinet;
      delete profil.disabled;
      delete profil.idCabinet;
      delete profil.idUtilisateur;
      delete profil.roles;
      delete profil.utilisateur;
      // Check if any field is null and if so, replace with empty string
      Object.keys(profil).forEach((key) => {
        if (profil[key] === null) {
          profil[key] = '';
        }
      });
      const res = await this.http.put(`${environment.basePath}/hypermed/profils/me`, profil, this.httpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public getCurrentProfil(): Profil | undefined {
    let rtd: Profil | undefined = undefined;
    const currentProfilStr = localStorage.getItem('currentProfil');
    if (currentProfilStr && currentProfilStr !== '') {
      rtd = JSON.parse(currentProfilStr);
    }
    return rtd;
  }

  /**
   * Indique si le profil courant doit utiliser l'API REST du proxy e-SanteConnect (DMP / INSi) plutôt que la websocket.
   */
  public useEdcRestProxy(): Promise<boolean> {
    const profilId = this.getCurrentProfil()?.id;
    const cache = this._useEdcRestProxyCache;
    if (cache && cache.profilId === profilId && Date.now() - cache.date < ProfilService.USE_EDC_REST_PROXY_CACHE_DELAY_MS) {
      return cache.value;
    }
    // Le back renvoie un body vide lorsque la valeur est false
    const value = this.http.get<boolean>(`${environment.basePath}/user/profil/useEdcRestProxy`, this.httpOptions).toPromise()
      .then(res => res.body === true)
      .catch(error => {
        this._useEdcRestProxyCache = undefined;
        throw error;
      });
    this._useEdcRestProxyCache = { profilId, date: Date.now(), value };
    return value;
  }

  public async updateUseEdcRestProxy(useEdcRestProxy: boolean): Promise<void> {
    await this.http.put<void>(`${environment.basePath}/user/profil/useEdcRestProxy`, { useEdcRestProxy }, this.httpOptions).toPromise();
    this._useEdcRestProxyCache = { profilId: this.getCurrentProfil()?.id, date: Date.now(), value: Promise.resolve(useEdcRestProxy) };
  }

  public async resetPassword(id: string): Promise<boolean> {
    try {
      const res = await this.http.post<void>(`${environment.basePath}/hypermed/profils/${id}/resetPassword`, undefined, {...this.httpOptions, observe:"response"}).toPromise();
      return res.status === 204;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }
}
